import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'routes/path';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { OrderValue, SortOrder, TableParams } from 'types/common';
import { useDidMount } from 'hooks/useDidMount';
import { BackdropLoader } from 'components/@shared/Loaders';
import TextField from '@mui/material/TextField';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { mainHeaderHeight } from 'theme/constants';
import { NoListYet } from 'components/@shared/NoListYet';
import { NoResultsFound } from 'components/@shared/NoResultsFound';
import { useModal } from 'hooks/useModal';
import { WarningPopUp } from 'components/@shared/WarningPopUp';
import { parametrizeURL } from 'routes/utils';
import { UserResponseViewModel } from 'types/user';
import { deleteUser, getUsers } from 'services/userService';
import { State, initialUsersTableParams, initialState, tableColumns, UsersTableParams } from './constants';
import { UserListRow } from 'components/Users/List/Row';
import { UsersActionsMenu } from 'components/Users/List/ItemActionsMenu';
import AddRounded from '@mui/icons-material/AddRounded';
import Button from '@mui/material/Button';
import { TableSwitch } from 'components/@shared/TableSwitch';
import { getHasFilterParams } from 'utils/getHasFilterParams';
import { getFilterParams } from 'pages/Appointments/utils';
import { ONLY_CONTRACTOR } from 'constants/roles';
import { useDeniedAccess } from 'hooks/useDeniedAccess';
import { AdaptiveTable } from 'components/@shared/TableRemotePagination/AdaptiveTable';
import { ITableHeadCell } from 'components/@shared/TableRemotePagination/types';
import { UserListCell } from 'components/Users/List/Row/UserListCell';
import { Headline } from 'components/@shared/Headline';

export const UsersPage = () => {
    const [state, setState] = useState<State>(initialState);
    const [selectedItem, setSelectedItem] = useState({ name: '', id: '' });
    const navigate = useNavigate();

    const removeModal = useModal();

    const loadList = useCallback(
        (param: Partial<UsersTableParams>) => {
            setState((prevState) => ({ ...prevState, isLoading: true }));
            const updatedParams: UsersTableParams = { ...state.tableParams, ...param };
            getUsers({ ...updatedParams, sortingOrder: Number(updatedParams.sortOrder === 'asc') as SortOrder })
                .then(({ items, count }) => {
                    setState((prevState) => ({
                        ...prevState,
                        isLoading: false,
                        list: items,
                        tableParams: updatedParams,
                        totalCount: count,
                    }));
                })
                .catch(() => {
                    setState((prevState) => ({ ...prevState, isLoading: false }));
                });
        },
        [state.tableParams],
    );

    const sortHandler = useCallback(
        (sortOrder: OrderValue, orderBy: string) => {
            loadList({ sortOrder, orderBy });
        },
        [loadList],
    );

    const setPageHandler = useCallback(
        (page: number) => {
            loadList({ page });
        },
        [loadList],
    );

    const setRowsPerPageHandler = useCallback(
        (pageSize: number) => {
            loadList({ page: 0, pageSize });
        },
        [loadList],
    );

    const searchChangeHandler = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        loadList({ page: 0, search: event.target.value });
    }, 500);

    const confirmDeleteHandler = useCallback(() => {
        removeModal.closeModal();
        setState((prevState) => ({ ...prevState, isLoading: true }));
        deleteUser(selectedItem.id)
            .then(() => loadList(initialUsersTableParams))
            .catch(() => {
                setState((prevState) => ({ ...prevState, isLoading: false }));
            });
    }, [state, removeModal, selectedItem]);

    const editHandler = useCallback(
        (id: string) => {
            navigate(parametrizeURL(PATH.USER_UPDATE, { id }));
        },
        [navigate],
    );

    const goToCreateUserHandler = useCallback(() => {
        navigate(PATH.USER_CREATE);
    }, [navigate]);

    const extraComponents = useMemo(
        () => ({
            Actions: ({ rowData }: { rowData: UserResponseViewModel }) => {
                const deleteHandler = (id: string) => {
                    setSelectedItem({ id, name: `${rowData.firstName} ${rowData.lastName}` });
                    removeModal.openModal();
                };

                return <UsersActionsMenu id={rowData.id} onDelete={deleteHandler} onEdit={editHandler} />;
            },
            Row: ({ rowData }: { rowData: UserResponseViewModel }) => (
                <UserListRow {...{ rowData, columns: tableColumns }} />
            ),
            Cell: ({ rowData, column }: { rowData: UserResponseViewModel; column: ITableHeadCell }) => (
                <UserListCell {...{ rowData, item: column }} />
            ),
        }),
        [removeModal, editHandler],
    );

    const applyActiveStatusHandler = useCallback(
        (activeStatus: boolean) => {
            loadList({ page: 0, activeStatus });
        },
        [loadList],
    );

    const filterParams = useMemo(() => getFilterParams(state.tableParams), [state.tableParams]);

    useDidMount(() => {
        loadList(state.tableParams);
    });

    useDeniedAccess(ONLY_CONTRACTOR);

    if (state.list.length === 0 && !state.isLoading && !getHasFilterParams(filterParams)) {
        return (
            <Box height={`calc(100vh - ${mainHeaderHeight}px)`}>
                <NoListYet description="Create a new user" buttonText="Create" onClick={goToCreateUserHandler} />
            </Box>
        );
    }
    return (
        <Box
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            px={2.5}
            py={2}
        >
            <Headline title="Users" />
            <Box width="100%">
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    mb={2}
                    flexWrap="wrap"
                    gap={2}
                    rowGap={1}
                >
                    <Button variant="outlined" startIcon={<AddRounded />} onClick={goToCreateUserHandler}>
                        Create user
                    </Button>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <TableSwitch
                                label="Active"
                                isActive={state.tableParams.activeStatus}
                                onChange={applyActiveStatusHandler}
                            />
                        </Box>
                        <TextField onChange={searchChangeHandler} size="small" label="Search">
                            Search
                        </TextField>
                    </Box>
                </Box>
                {!!state.list.length && (
                    <AdaptiveTable
                        rows={state.list}
                        columns={tableColumns}
                        tableParams={state.tableParams}
                        totalCount={state.totalCount}
                        components={extraComponents}
                        onSort={sortHandler}
                        onSetPage={setPageHandler}
                        onSetRowsPerPage={setRowsPerPageHandler}
                    />
                )}
                {state.list.length === 0 && getHasFilterParams(filterParams) && <NoResultsFound />}
            </Box>
            {removeModal.open && (
                <WarningPopUp
                    isOpen={removeModal.open}
                    warningHeader="Remove User?"
                    warningBody={`Are you sure you want to remove ${selectedItem.name}?`}
                    confirmButtonText="Remove"
                    onClose={removeModal.closeModal}
                    onConfirm={confirmDeleteHandler}
                />
            )}
            {state.isLoading && <BackdropLoader />}
        </Box>
    );
};
